import React from 'react';

import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleCardTemplate from '@/components/layouts/SingleCardTemplate';
import Loading from '@/components/ui/loading/Loading';
import { pageTitles } from '@/constants/pageTitles';
import useRegisterInvitation from '@/hooks/invitations/useRegisterInvitation';
import useValidateInvitation from '@/hooks/invitations/useValidateInvitation';

import { PARENTS_LIMIT_EXCEEDED_ERROR_CODE } from '../constants';
import { getInvitationToken } from '../invitationToken';

import InvitationRegisterConfirmForm from './InvitationRegisterConfirmForm';
import { type InvitationRegisterFormValues } from './InvitationRegisterInputForm';
import InvitationValidateErrorPage from './InvitationValidateErrorPage';

const requiredFields: (keyof InvitationRegisterFormValues)[] = [
  'email',
  'password',
  'first_name',
  'last_name',
  'first_name_kana',
  'last_name_kana',
];

interface Props {
  formValues: InvitationRegisterFormValues | null;
  onBack: () => void;
  onRegistered: () => void;
  onRegisterFailed: () => void;
}

const InvitationRegisterConfirmContent: React.FC<
  Props & { invitationToken: string }
> = ({
  invitationToken,
  formValues,
  onBack,
  onRegistered,
  onRegisterFailed,
}) => {
  const {
    data: validateData,
    isFetching: isValidating,
    error: validateError,
  } = useValidateInvitation({
    invitationToken,
  });
  const student = validateData?.student;
  const {
    mutate,
    isPending: isRegistering,
    error: registerError,
  } = useRegisterInvitation();

  const apiError =
    registerError && registerError.status >= 500
      ? ['予期しないエラーが発生しました。時間をおいて再度お試しください']
      : undefined;

  // 「戻る」の後の「進む」や直接のアクセスでは、フォームの値（パスワード）が無い
  if (!formValues || !requiredFields.every((field) => formValues[field])) {
    return <Navigate to="/invitations/register/input" replace />;
  }

  if (isValidating) {
    return (
      <SingleCardTemplate title={pageTitles.invitationRegisterConfirm}>
        <Loading />
      </SingleCardTemplate>
    );
  }

  if (validateError) {
    if (validateError.error_code === PARENTS_LIMIT_EXCEEDED_ERROR_CODE) {
      return (
        <InvitationValidateErrorPage
          type="parents_limit"
          pageTitle={pageTitles.invitationRegisterConfirm}
        />
      );
    }
    if (validateError.status === 403) {
      return (
        <InvitationValidateErrorPage
          type="expired"
          pageTitle={pageTitles.invitationRegisterConfirm}
        />
      );
    }
    if (validateError.status === 400 || validateError.status === 422) {
      return (
        <InvitationValidateErrorPage
          type="invalid"
          pageTitle={pageTitles.invitationRegisterConfirm}
        />
      );
    }
    if (validateError.status >= 500) {
      return <ErrorPage type="internal_server_error" />;
    }
    return <ErrorPage />;
  }

  if (!student) {
    return <ErrorPage type="not_found" />;
  }

  const handleSubmit = () => {
    mutate(
      JSON.stringify({
        invitation_token: invitationToken,
        parent: {
          password: formValues.password,
          password_confirmation: formValues.password_confirmation,
          last_name: formValues.last_name,
          first_name: formValues.first_name,
          last_name_kana: formValues.last_name_kana,
          first_name_kana: formValues.first_name_kana,
        },
      }),
      {
        onSuccess: onRegistered,
        onError: (err) => {
          if (err.status < 500) {
            onRegisterFailed();
          }
        },
      },
    );
  };

  return (
    <SingleCardTemplate title={pageTitles.invitationRegisterConfirm}>
      <Helmet>
        <title>{pageTitles.invitationRegisterConfirm}</title>
      </Helmet>
      <InvitationRegisterConfirmForm
        formValues={formValues}
        student={student}
        apiError={apiError}
        submitButtonDisabled={isRegistering}
        onBack={onBack}
        onSubmit={handleSubmit}
      />
    </SingleCardTemplate>
  );
};

const InvitationRegisterConfirmPage: React.FC<Props> = (props) => {
  const invitationToken = getInvitationToken();

  if (!invitationToken) {
    return (
      <InvitationValidateErrorPage
        type="token_missing"
        pageTitle={pageTitles.invitationRegisterConfirm}
      />
    );
  }

  return (
    <InvitationRegisterConfirmContent
      invitationToken={invitationToken}
      {...props}
    />
  );
};

export default InvitationRegisterConfirmPage;
